import { FileText, Lightbulb, MessageSquare, ClipboardCheck } from "lucide-react";
import { Sparkles } from "lucide-react";
import type { LucideIcon } from "lucide-react";

interface UseCase {
  icon: LucideIcon;
  title: string;
  description: string;
  example: string;
}

const useCases: UseCase[] = [
  {
    icon: Lightbulb,
    title: "Idea to requirements",
    description: "Turn a rough feature thought into structured requirements, stories, and acceptance criteria.",
    example: '"We need smarter rider assignment"',
  },
  {
    icon: FileText,
    title: "PRD review",
    description: "Review an existing draft for ambiguity, missing decisions, weak assumptions, and testability gaps.",
    example: '"Is this ready for engineering?"',
  },
  {
    icon: ClipboardCheck,
    title: "Readiness before handoff",
    description: "Surface the questions engineering will ask before the requirement reaches planning or implementation.",
    example: '"What is still unclear here?"',
  },
  {
    icon: MessageSquare,
    title: "Refinement loop",
    description: "Use natural-language prompts to improve specific stories, edge cases, acceptance criteria, and export formats.",
    example: '"Make this more testable"',
  },
];

export function UseCaseCards() {
  return (
    <section className="relative overflow-hidden py-14 md:py-20">
      <div className="absolute bottom-[20%] right-[10%] h-[400px] w-[400px] rounded-full bg-primary/[0.03] blur-[140px]" />

      <div className="container relative z-10 px-4 md:px-6">
        <div className="mx-auto mb-10 max-w-2xl text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
            <Sparkles className="h-3.5 w-3.5" />
            Use cases
          </div>
          <h2 className="mb-3 font-sans text-2xl font-bold md:text-3xl">Useful before the expensive part starts</h2>
          <p className="text-base text-muted-foreground">
            Reqqy fits the moments where requirement quality starts affecting delivery speed.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {useCases.map((useCase, index) => (
            <div
              key={useCase.title}
              className="group relative rounded-xl border border-border/30 glass p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/20"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="absolute inset-0 rounded-xl bg-gradient-to-b from-primary/[0.02] to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              <div className="relative z-10">
                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <useCase.icon className="h-5 w-5 text-primary" />
                </div>
                <h3 className="mb-2 text-base font-semibold">{useCase.title}</h3>
                <p className="mb-4 text-sm leading-relaxed text-muted-foreground">{useCase.description}</p>
                <div className="rounded-lg border border-border/30 bg-background/40 p-3 text-xs italic text-muted-foreground">
                  {useCase.example}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
